import * as THREE from "three";

/**
 * OneEuroFilter — Adaptive low-pass filter (Casiez et al., ACM CHI 2012).
 * Eliminates high-frequency webcam sensor jitter when stationary (low cutoff frequency)
 * while eliminating latency/lag during movement (cutoff frequency scales dynamically with speed).
 */
class OneEuroFilter {
  constructor({ minCutoff = 0.35, beta = 2.0, dCutoff = 1.0 } = {}) {
    this.minCutoff = minCutoff; // lower = rock-solid at rest (Hz)
    this.beta = beta;           // higher = faster response / zero lag during motion
    this.dCutoff = dCutoff;     // cutoff for derivative estimation (Hz)
    this.xPrev = null;
    this.dxPrev = 0;
    this.tPrev = null;
  }

  alpha(cutoff, dt) {
    const tau = 1.0 / (2 * Math.PI * cutoff);
    return 1.0 / (1.0 + tau / dt);
  }

  filter(x, t) {
    if (this.xPrev === null) {
      this.xPrev = x;
      this.tPrev = t;
      return x;
    }
    const dt = Math.max((t - this.tPrev) / 1000, 1e-4);
    this.tPrev = t;

    // Filter derivative (velocity)
    const dx = (x - this.xPrev) / dt;
    const aD = this.alpha(this.dCutoff, dt);
    const edx = this.dxPrev + aD * (dx - this.dxPrev);
    this.dxPrev = edx;

    // Adaptive cutoff based on speed
    const cutoff = this.minCutoff + this.beta * Math.abs(edx);
    const a = this.alpha(cutoff, dt);
    const filtered = this.xPrev + a * (x - this.xPrev);
    this.xPrev = filtered;
    return filtered;
  }

  reset() {
    this.xPrev = null;
    this.dxPrev = 0;
    this.tPrev = null;
  }
}

/**
 * Smooth, velocity-adaptive angle interpolation (Slerp on 1D circle [-PI, PI]).
 * - Always rotates along the shortest circular arc (zero flips or singularities).
 * - Dynamically adapts to user velocity:
 *   When moving slowly (small diff): alpha is gentle (minAlpha ~0.38) for smooth, tremor-free motion.
 *   When moving fast (large diff): alpha scales up sharply (maxAlpha ~0.90), matching high-speed
 *   user gestures in real-time with zero lag or drag.
 */
function slerpAngle(current, target, minAlpha = 0.38, maxAlpha = 0.90, deadband = 0.006) {
  if (current === null || current === undefined) return target;
  let diff = (target - current) % (Math.PI * 2);
  if (diff > Math.PI) diff -= Math.PI * 2;
  if (diff < -Math.PI) diff += Math.PI * 2;
  const absDiff = Math.abs(diff);
  if (absDiff < deadband) return current;

  // Speed ratio: 0 at rest, reaching 1.0 when angular gap is ~0.40 rad (~23 deg)
  const ratio = Math.min(1.0, absDiff / 0.40);
  const alpha = minAlpha + (maxAlpha - minAlpha) * (ratio * ratio);

  let next = current + diff * alpha;
  while (next > Math.PI) next -= Math.PI * 2;
  while (next < -Math.PI) next += Math.PI * 2;
  return next;
}



/**
 * Analytic 2-link IK solver for natural knee bending.
 * Given hip, foot, and limb lengths, returns the knee joint position.
 * bendSide: +1 for bending right/outward, -1 for bending left/outward.
 */
function solveKnee(hip, foot, L1, L2, bendSide) {
  const dx = foot[0] - hip[0];
  const dy = foot[1] - hip[1];
  const d = Math.hypot(dx, dy) || 1;
  const clampedD = Math.min(d, (L1 + L2) * 0.999);
  const cosAlpha = (L1 * L1 + clampedD * clampedD - L2 * L2) / (2 * L1 * clampedD);
  const alpha = Math.acos(Math.max(-1, Math.min(1, cosAlpha)));
  const baseAngle = Math.atan2(dy, dx);
  const kneeAngle = baseAngle + bendSide * alpha;
  return [hip[0] + L1 * Math.cos(kneeAngle), hip[1] + L1 * Math.sin(kneeAngle)];
}

/**
 * StickFigure — 2D canvas generator rendering real-time pose as a canonical
 * full stick figure of fixed size, driven by continuous 2D vector kinematics.
 * Arms rotate fluidly in 360 degrees following user movements, with long elegant
 * reach and clean classic round hands.
 */
export class StickFigure {
  constructor({ width = 1152, height = 512 } = {}) {
    this.width = width;
    this.height = height;

    this.canvas = document.createElement("canvas");
    this.canvas.width = this.width;
    this.canvas.height = this.height;
    this.ctx = this.canvas.getContext("2d");

    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.minFilter = THREE.LinearFilter;

    this.currentLandmarks = null;
    this.dirty = true;

    // 1 Euro Filters for each pose landmark.
    // Wrists (15, 16), ankles (27, 28), feet (31, 32), knees (25, 26), elbows (13, 14)
    // use high beta for zero-lag responsiveness on fast dance gestures.
    this.lmFiltersX = Array.from({ length: 33 }, (_, i) => {
      const isExtremeLimb = i === 15 || i === 16 || i === 27 || i === 28 || i === 31 || i === 32; // hands & feet
      const isMidLimb = i === 13 || i === 14 || i === 25 || i === 26; // elbows & knees
      const beta = isExtremeLimb ? 6.5 : (isMidLimb ? 4.8 : 2.4);
      return new OneEuroFilter({ minCutoff: 0.35, beta });
    });
    this.lmFiltersY = Array.from({ length: 33 }, (_, i) => {
      const isExtremeLimb = i === 15 || i === 16 || i === 27 || i === 28 || i === 31 || i === 32;
      const isMidLimb = i === 13 || i === 14 || i === 25 || i === 26;
      const beta = isExtremeLimb ? 6.5 : (isMidLimb ? 4.8 : 2.4);
      return new OneEuroFilter({ minCutoff: 0.35, beta });
    });

    // Torso tilt and head offset filters (kills posture trembling)
    this.tiltFilter = new OneEuroFilter({ minCutoff: 0.20, beta: 1.8 });
    this.headFilter = new OneEuroFilter({ minCutoff: 0.25, beta: 1.8 });

    // Joint angles for smooth 360-degree rotation (zero flips, shortest-arc slerp)
    this.angleUpperR = null;
    this.angleLowerR = null;
    this.angleUpperL = null;
    this.angleLowerL = null;
    this.angleThighR = null;
    this.angleShinR = null;
    this.angleThighL = null;
    this.angleShinL = null;
    this.filteredLm = null;
    this.lastTilt = 0;
  }

  setLandmarks(landmarks) {
    if (!landmarks) {
      this.currentLandmarks = null;
      this.filteredLm = null;
      this.dirty = true;
      return;
    }

    // Filter landmarks exactly when received from the camera to measure true physical velocity
    const now = performance.now();
    const filtered = [];
    for (let i = 0; i < landmarks.length; i++) {
      const p = landmarks[i];
      if (!p) {
        filtered.push(null);
        continue;
      }
      const fx = this.lmFiltersX[i].filter(p.x, now);
      const fy = this.lmFiltersY[i].filter(p.y, now);
      filtered.push({ x: fx, y: fy, z: p.z || 0, visibility: p.visibility ?? 1 });
    }

    this.filteredLm = filtered;
    this.currentLandmarks = landmarks;
    this.dirty = true;
  }

  _resetFilters() {
    for (let i = 0; i < 33; i++) {
      this.lmFiltersX[i].reset();
      this.lmFiltersY[i].reset();
    }
    this.tiltFilter.reset();
    this.headFilter.reset();
    this.angleUpperR = null;
    this.angleLowerR = null;
    this.angleUpperL = null;
    this.angleLowerL = null;
    this.angleThighR = null;
    this.angleShinR = null;
    this.angleThighL = null;
    this.angleShinL = null;
    this.filteredLm = null;
    this.lastTilt = 0;
  }

  update(t = 0) {
    // Only re-render when dirty or when a dancer is active (to drive the dance groove)
    if (!this.dirty && !this.currentLandmarks) return;
    this.dirty = false;

    const c = this.ctx;
    const w = this.width;
    const h = this.height;

    // Background: clean paper-white with soft blueprint grid
    c.filter = "none";
    c.fillStyle = "#f5f5f2";
    c.fillRect(0, 0, w, h);
    c.strokeStyle = "rgba(10,10,15,0.05)";
    c.lineWidth = 1;
    for (let x = 0; x < w; x += 48) {
      c.beginPath(); c.moveTo(x, 0); c.lineTo(x, h); c.stroke();
    }
    for (let y = 0; y < h; y += 48) {
      c.beginPath(); c.moveTo(0, y); c.lineTo(w, y); c.stroke();
    }

    // Top status header
    c.fillStyle = "#0a0a0f";
    c.font = "700 22px 'JetBrains Mono', monospace";
    c.fillText("DARD-E-DISCO // dance floor", 24, 38);
    c.fillStyle = "#ff2e88";
    c.beginPath(); c.arc(w - 36, 30, 6, 0, Math.PI * 2); c.fill();
    c.fillStyle = "#0a0a0f";
    c.font = "500 14px 'JetBrains Mono', monospace";
    c.fillText("● live", w - 96, 34);

    if (!this.filteredLm) {
      this._resetFilters();
      c.fillStyle = "#8f8f88";
      c.font = "italic 32px 'Instrument Serif', serif";
      c.textAlign = "center";
      c.fillText("step in front of your camera —", w / 2, h / 2 - 10);
      c.fillText("your shadow will dance here.", w / 2, h / 2 + 32);
      c.textAlign = "start";
      this.texture.needsUpdate = true;
      return;
    }

    const lm = this.filteredLm;
    const now = performance.now();

    // Check minimum visibility (at least one shoulder or head visible)
    const hasRSh = lm[12] && lm[12].visibility >= 0.35;
    const hasLSh = lm[11] && lm[11].visibility >= 0.35;
    const hasNose = lm[0] && lm[0].visibility >= 0.35;

    if (!hasRSh && !hasLSh && !hasNose) {
      c.fillStyle = "#8f8f88";
      c.font = "italic 30px 'Instrument Serif', serif";
      c.textAlign = "center";
      c.fillText("looking for you —", w / 2, h / 2);
      c.textAlign = "start";
      this.texture.needsUpdate = true;
      return;
    }

    // =========================================================================
    // CANONICAL SKELETON SPEC (Longer elegant limbs & classic circular hands)
    // =========================================================================
    const X_CENTER = w / 2; // Fixed horizontal anchor
    const Y_CENTER = 260;   // Torso spine center
    const H_TORSO = 105;    // Torso height
    const W_SH = 40;        // Shoulder half-width (80px total width)
    const W_HIP = 30;       // Hip half-width (60px total width)
    const L_UPPER_ARM = 92; // Elongated upper arm (was 72)
    const L_FOREARM = 88;   // Elongated forearm (was 66)
    const HAND_R = 17;      // Clean circular hand
    const L_THIGH = 88;     // Thigh length
    const L_SHIN = 84;      // Shin length
    const LIMB_W = 28;      // Consistent limb stroke width
    const FOOT_W = 32;      // Shoe width (wide disco shoe)
    const FOOT_H = 15;      // Shoe height
    const HEAD_R = 36;      // Head radius
    const NECK_W = 22;      // Neck width
    const FLOOR_Y = 484;    // Canonical floor plane

    // 1) Torso tilt calculation from shoulder line
    let userTilt = 0;
    if (hasRSh && hasLSh) {
      const dx = (1 - lm[12].x) - (1 - lm[11].x);
      const dy = lm[12].y - lm[11].y;
      const rawTilt = Math.atan2(dy, dx);
      // Clamp tilt (+/- 25 deg) and filter with 1 Euro Filter
      const clampedTilt = Math.max(-0.45, Math.min(0.45, rawTilt));
      const filteredTilt = this.tiltFilter.filter(clampedTilt, now);
      // Deadband: freeze tilt if change is < 0.01 rad (0.5 deg)
      if (Math.abs(filteredTilt - this.lastTilt) > 0.01) {
        this.lastTilt = filteredTilt;
      }
      userTilt = this.lastTilt;
    }

    // 2) Legs visibility check: are real legs detected in camera?
    const hasRealRLeg = lm[24] && lm[26] && lm[24].visibility >= 0.40 && lm[26].visibility >= 0.40;
    const hasRealLLeg = lm[23] && lm[25] && lm[23].visibility >= 0.40 && lm[25].visibility >= 0.40;
    const hasRealLegs = hasRealRLeg || hasRealLLeg;

    // 3) AUTHENTIC DISCO DANCE GROOVE (When seated or legs out of frame)
    // Tempo: ~118 BPM disco club 2-step (confident hip sway + harmonic beat dip)
    let swayX = 0;
    let dipY = 0;
    let grooveTilt = 0;

    const discoBps = 118 / 60; // ~1.97 beats per second
    const discoPhase = t * Math.PI * discoBps;

    if (!hasRealLegs) {
      // Confident, wide-stance disco weight shift
      swayX = Math.sin(discoPhase) * 22; // smooth 22px hip swing
      dipY = (1 - Math.cos(2 * discoPhase)) * 4.5; // harmonic dip on each beat (0 to 9px)
      grooveTilt = -Math.sin(discoPhase) * 0.05; // upper body counter-balances hip sway
    }

    const totalTilt = userTilt + grooveTilt;
    const cosT = Math.cos(totalTilt);
    const sinT = Math.sin(totalTilt);

    // Torso spine anchors
    const torsoTopCenter = [X_CENTER + swayX * 0.35, Y_CENTER - (H_TORSO / 2) + dipY * 0.4];
    const torsoBottomCenter = [X_CENTER + swayX, Y_CENTER + (H_TORSO / 2) + dipY];

    // Torso 4 corner points
    const torsoRSh = [torsoTopCenter[0] + W_SH * cosT, torsoTopCenter[1] + W_SH * sinT];
    const torsoLSh = [torsoTopCenter[0] - W_SH * cosT, torsoTopCenter[1] - W_SH * sinT];
    const torsoRHip = [torsoBottomCenter[0] + W_HIP * cosT, torsoBottomCenter[1] + W_HIP * sinT];
    const torsoLHip = [torsoBottomCenter[0] - W_HIP * cosT, torsoBottomCenter[1] - W_HIP * sinT];

    // =========================================================================
    // FORWARD KINEMATICS — CONTINUOUS 360-DEGREE ANGULAR ROTATION (ZERO FLIPPING)
    // =========================================================================

    // Right Arm (Elbow & Wrist)
    let elbowR = null;
    let wristR = null;
    const hasRElbow = lm[14] && lm[14].visibility >= 0.35;
    const hasRWrist = lm[16] && lm[16].visibility >= 0.35;

    // Right Upper Arm Target Angle (with direct wrist fallback if elbow blinks)
    let targetUpperR = null;
    if (hasRSh && hasRElbow) {
      const dx = (1 - lm[14].x) - (1 - lm[12].x);
      const dy = lm[14].y - lm[12].y;
      if (Math.hypot(dx, dy) > 0.03) targetUpperR = Math.atan2(dy, dx);
    } else if (hasRSh && hasRWrist) {
      const dx = (1 - lm[16].x) - (1 - lm[12].x);
      const dy = lm[16].y - lm[12].y;
      if (Math.hypot(dx, dy) > 0.05) targetUpperR = Math.atan2(dy, dx);
    }

    if (targetUpperR !== null) {
      this.angleUpperR = slerpAngle(this.angleUpperR, targetUpperR, 0.38, 0.90);
    } else {
      // Natural resting pose with gentle disco sway
      const armSway = Math.sin(discoPhase) * 0.08;
      const restUpper = Math.PI * 0.46 + armSway;
      this.angleUpperR = slerpAngle(this.angleUpperR, restUpper, 0.16, 0.16);
    }

    if (this.angleUpperR === null) this.angleUpperR = Math.PI * 0.46;

    elbowR = [
      torsoRSh[0] + L_UPPER_ARM * Math.cos(this.angleUpperR),
      torsoRSh[1] + L_UPPER_ARM * Math.sin(this.angleUpperR)
    ];

    // Right Forearm Target Angle
    let targetLowerR = null;
    if (hasRElbow && hasRWrist) {
      const dx = (1 - lm[16].x) - (1 - lm[14].x);
      const dy = lm[16].y - lm[14].y;
      if (Math.hypot(dx, dy) > 0.03) targetLowerR = Math.atan2(dy, dx);
    }

    if (targetLowerR !== null) {
      this.angleLowerR = slerpAngle(this.angleLowerR, targetLowerR, 0.38, 0.90);
    } else {
      // Forearm naturally follows upper arm with slight natural bend
      const restLower = this.angleUpperR + 0.08;
      this.angleLowerR = slerpAngle(this.angleLowerR, restLower, 0.18, 0.18);
    }

    if (this.angleLowerR === null) this.angleLowerR = this.angleUpperR;

    wristR = [
      elbowR[0] + L_FOREARM * Math.cos(this.angleLowerR),
      elbowR[1] + L_FOREARM * Math.sin(this.angleLowerR)
    ];

    // Left Arm (Elbow & Wrist)
    let elbowL = null;
    let wristL = null;
    const hasLElbow = lm[13] && lm[13].visibility >= 0.35;
    const hasLWrist = lm[15] && lm[15].visibility >= 0.35;

    // Left Upper Arm Target Angle (with direct wrist fallback if elbow blinks)
    let targetUpperL = null;
    if (hasLSh && hasLElbow) {
      const dx = (1 - lm[13].x) - (1 - lm[11].x);
      const dy = lm[13].y - lm[11].y;
      if (Math.hypot(dx, dy) > 0.03) targetUpperL = Math.atan2(dy, dx);
    } else if (hasLSh && hasLWrist) {
      const dx = (1 - lm[15].x) - (1 - lm[11].x);
      const dy = lm[15].y - lm[11].y;
      if (Math.hypot(dx, dy) > 0.05) targetUpperL = Math.atan2(dy, dx);
    }

    if (targetUpperL !== null) {
      this.angleUpperL = slerpAngle(this.angleUpperL, targetUpperL, 0.38, 0.90);
    } else {
      // Natural resting pose with gentle disco sway
      const armSway = Math.sin(discoPhase) * 0.08;
      const restUpper = Math.PI * 0.54 + armSway;
      this.angleUpperL = slerpAngle(this.angleUpperL, restUpper, 0.16, 0.16);
    }

    if (this.angleUpperL === null) this.angleUpperL = Math.PI * 0.54;

    elbowL = [
      torsoLSh[0] + L_UPPER_ARM * Math.cos(this.angleUpperL),
      torsoLSh[1] + L_UPPER_ARM * Math.sin(this.angleUpperL)
    ];

    // Left Forearm Target Angle
    let targetLowerL = null;
    if (hasLElbow && hasLWrist) {
      const dx = (1 - lm[15].x) - (1 - lm[13].x);
      const dy = lm[15].y - lm[13].y;
      if (Math.hypot(dx, dy) > 0.03) targetLowerL = Math.atan2(dy, dx);
    }

    if (targetLowerL !== null) {
      this.angleLowerL = slerpAngle(this.angleLowerL, targetLowerL, 0.38, 0.90);
    } else {
      // Forearm naturally follows upper arm with slight natural bend
      const restLower = this.angleUpperL - 0.08;
      this.angleLowerL = slerpAngle(this.angleLowerL, restLower, 0.18, 0.18);
    }

    if (this.angleLowerL === null) this.angleLowerL = this.angleUpperL;

    wristL = [
      elbowL[0] + L_FOREARM * Math.cos(this.angleLowerL),
      elbowL[1] + L_FOREARM * Math.sin(this.angleLowerL)
    ];

    // =========================================================================
    // LEGS — VELOCITY-ADAPTIVE REAL TRACKING & SEAMLESS DISCO GROOVE
    // =========================================================================
    // Pre-calculate canonical idle disco groove poses
    const tapR = Math.max(0, -Math.sin(discoPhase)) * 8;
    const tapL = Math.max(0, Math.sin(discoPhase)) * 8;
    const idleFootR = [X_CENTER + 78 + swayX * 0.15, FLOOR_Y - tapR];
    const idleFootL = [X_CENTER - 78 + swayX * 0.15, FLOOR_Y - tapL];
    const idleKneeR = solveKnee(torsoRHip, idleFootR, L_THIGH, L_SHIN, 1);
    const idleKneeL = solveKnee(torsoLHip, idleFootL, L_THIGH, L_SHIN, -1);

    const idleThighAngleR = Math.atan2(idleKneeR[1] - torsoRHip[1], idleKneeR[0] - torsoRHip[0]);
    const idleShinAngleR = Math.atan2(idleFootR[1] - idleKneeR[1], idleFootR[0] - idleKneeR[0]);
    const idleThighAngleL = Math.atan2(idleKneeL[1] - torsoLHip[1], idleKneeL[0] - torsoLHip[0]);
    const idleShinAngleL = Math.atan2(idleFootL[1] - idleKneeL[1], idleFootL[0] - idleKneeL[0]);

    // --- Right Leg Target Angles ---
    let targetThighR = null;
    let targetShinR = null;
    if (hasRealRLeg) {
      const dxThigh = (1 - lm[26].x) - (1 - lm[24].x);
      const dyThigh = lm[26].y - lm[24].y;
      if (Math.hypot(dxThigh, dyThigh) > 0.03) targetThighR = Math.atan2(dyThigh, dxThigh);

      if (lm[28] && lm[28].visibility >= 0.35) {
        const dxShin = (1 - lm[28].x) - (1 - lm[26].x);
        const dyShin = lm[28].y - lm[26].y;
        if (Math.hypot(dxShin, dyShin) > 0.03) targetShinR = Math.atan2(dyShin, dxShin);
      }
    }

    if (targetThighR !== null) {
      this.angleThighR = slerpAngle(this.angleThighR, targetThighR, 0.38, 0.90);
    } else {
      this.angleThighR = slerpAngle(this.angleThighR, idleThighAngleR, 0.16, 0.16);
    }
    if (this.angleThighR === null) this.angleThighR = idleThighAngleR;

    if (targetShinR !== null) {
      this.angleShinR = slerpAngle(this.angleShinR, targetShinR, 0.38, 0.90);
    } else if (targetThighR !== null) {
      this.angleShinR = slerpAngle(this.angleShinR, this.angleThighR + 0.06, 0.25, 0.60);
    } else {
      this.angleShinR = slerpAngle(this.angleShinR, idleShinAngleR, 0.16, 0.16);
    }
    if (this.angleShinR === null) this.angleShinR = idleShinAngleR;

    const kneeR = [
      torsoRHip[0] + L_THIGH * Math.cos(this.angleThighR),
      torsoRHip[1] + L_THIGH * Math.sin(this.angleThighR),
    ];
    const ankleR = [
      kneeR[0] + L_SHIN * Math.cos(this.angleShinR),
      kneeR[1] + L_SHIN * Math.sin(this.angleShinR),
    ];
    const shoeAngleR = hasRealRLeg ? (this.angleShinR - Math.PI / 2 + 0.18) : (0.20 + (tapR > 1 ? 0.08 : 0));

    // --- Left Leg Target Angles ---
    let targetThighL = null;
    let targetShinL = null;
    if (hasRealLLeg) {
      const dxThigh = (1 - lm[25].x) - (1 - lm[23].x);
      const dyThigh = lm[25].y - lm[23].y;
      if (Math.hypot(dxThigh, dyThigh) > 0.03) targetThighL = Math.atan2(dyThigh, dxThigh);

      if (lm[27] && lm[27].visibility >= 0.35) {
        const dxShin = (1 - lm[27].x) - (1 - lm[25].x);
        const dyShin = lm[27].y - lm[25].y;
        if (Math.hypot(dxShin, dyShin) > 0.03) targetShinL = Math.atan2(dyShin, dxShin);
      }
    }

    if (targetThighL !== null) {
      this.angleThighL = slerpAngle(this.angleThighL, targetThighL, 0.38, 0.90);
    } else {
      this.angleThighL = slerpAngle(this.angleThighL, idleThighAngleL, 0.16, 0.16);
    }
    if (this.angleThighL === null) this.angleThighL = idleThighAngleL;

    if (targetShinL !== null) {
      this.angleShinL = slerpAngle(this.angleShinL, targetShinL, 0.38, 0.90);
    } else if (targetThighL !== null) {
      this.angleShinL = slerpAngle(this.angleShinL, this.angleThighL - 0.06, 0.25, 0.60);
    } else {
      this.angleShinL = slerpAngle(this.angleShinL, idleShinAngleL, 0.16, 0.16);
    }
    if (this.angleShinL === null) this.angleShinL = idleShinAngleL;

    const kneeL = [
      torsoLHip[0] + L_THIGH * Math.cos(this.angleThighL),
      torsoLHip[1] + L_THIGH * Math.sin(this.angleThighL),
    ];
    const ankleL = [
      kneeL[0] + L_SHIN * Math.cos(this.angleShinL),
      kneeL[1] + L_SHIN * Math.sin(this.angleShinL),
    ];
    const shoeAngleL = hasRealLLeg ? (this.angleShinL - Math.PI / 2 - 0.18) : (-0.20 - (tapL > 1 ? 0.08 : 0));

    // =========================================================================
    // HEAD & NECK
    // =========================================================================
    const neckLen = 20;
    const neckTop = [torsoTopCenter[0] + neckLen * sinT, torsoTopCenter[1] - neckLen * cosT];
    let headCenter = [neckTop[0] + HEAD_R * 0.85 * sinT, neckTop[1] - HEAD_R * 0.85 * cosT];

    // Smooth head turn tracking (stabilized by 1 Euro Filter)
    if (hasNose && hasRSh && hasLSh) {
      const shMidCamX = (lm[11].x + lm[12].x) / 2;
      const rawOffset = ((1 - lm[0].x) - (1 - shMidCamX)) * 50;
      const clampedOffset = Math.max(-14, Math.min(14, rawOffset));
      const filteredHeadOffset = this.headFilter.filter(clampedOffset, now);
      headCenter[0] += filteredHeadOffset;
    }

    // =========================================================================
    // RENDERING — SOFT-EDGED HOLOGRAPHIC SILHOUETTE
    // =========================================================================
    c.save();
    c.filter = "blur(2.5px)";
    c.fillStyle = "rgba(10, 10, 15, 0.94)";
    c.strokeStyle = "rgba(10, 10, 15, 0.94)";
    c.lineCap = "round";
    c.lineJoin = "round";

    const drawBone = (p1, p2, width) => {
      if (!p1 || !p2) return;
      c.lineWidth = width;
      c.beginPath();
      c.moveTo(p1[0], p1[1]);
      c.lineTo(p2[0], p2[1]);
      c.stroke();
    };

    // 1) TORSO POLYGON
    c.beginPath();
    c.moveTo(torsoLSh[0], torsoLSh[1]);
    c.lineTo(torsoRSh[0], torsoRSh[1]);
    c.lineTo(torsoRHip[0], torsoRHip[1]);
    c.lineTo(torsoLHip[0], torsoLHip[1]);
    c.closePath();
    c.fill();

    // 2) JOINT CAPS (Shoulders & Hips)
    c.beginPath(); c.arc(torsoRSh[0], torsoRSh[1], LIMB_W * 0.5, 0, Math.PI * 2); c.fill();
    c.beginPath(); c.arc(torsoLSh[0], torsoLSh[1], LIMB_W * 0.5, 0, Math.PI * 2); c.fill();
    c.beginPath(); c.arc(torsoRHip[0], torsoRHip[1], LIMB_W * 0.5, 0, Math.PI * 2); c.fill();
    c.beginPath(); c.arc(torsoLHip[0], torsoLHip[1], LIMB_W * 0.5, 0, Math.PI * 2); c.fill();

    // 3) ARMS & HANDS (Classic sleek round hands directly at wrist)
    // Right Arm
    drawBone(torsoRSh, elbowR, LIMB_W);
    c.beginPath(); c.arc(elbowR[0], elbowR[1], LIMB_W * 0.5, 0, Math.PI * 2); c.fill();
    drawBone(elbowR, wristR, LIMB_W * 0.88);
    c.beginPath(); c.arc(wristR[0], wristR[1], HAND_R, 0, Math.PI * 2); c.fill();

    // Left Arm
    drawBone(torsoLSh, elbowL, LIMB_W);
    c.beginPath(); c.arc(elbowL[0], elbowL[1], LIMB_W * 0.5, 0, Math.PI * 2); c.fill();
    drawBone(elbowL, wristL, LIMB_W * 0.88);
    c.beginPath(); c.arc(wristL[0], wristL[1], HAND_R, 0, Math.PI * 2); c.fill();

    // 4) LEGS
    // Right Leg
    drawBone(torsoRHip, kneeR, LIMB_W);
    c.beginPath(); c.arc(kneeR[0], kneeR[1], LIMB_W * 0.5, 0, Math.PI * 2); c.fill();
    drawBone(kneeR, ankleR, LIMB_W * 0.88);
    c.beginPath();
    c.ellipse(ankleR[0] + 8, ankleR[1] + 6, FOOT_W, FOOT_H, shoeAngleR, 0, Math.PI * 2);
    c.fill();

    // Left Leg
    drawBone(torsoLHip, kneeL, LIMB_W);
    c.beginPath(); c.arc(kneeL[0], kneeL[1], LIMB_W * 0.5, 0, Math.PI * 2); c.fill();
    drawBone(kneeL, ankleL, LIMB_W * 0.88);
    c.beginPath();
    c.ellipse(ankleL[0] - 8, ankleL[1] + 6, FOOT_W, FOOT_H, shoeAngleL, 0, Math.PI * 2);
    c.fill();

    // 5) NECK & HEAD
    c.lineWidth = NECK_W;
    c.beginPath();
    c.moveTo(torsoTopCenter[0], torsoTopCenter[1]);
    c.lineTo(neckTop[0], neckTop[1]);
    c.stroke();

    c.beginPath();
    c.arc(headCenter[0], headCenter[1], HEAD_R, 0, Math.PI * 2);
    c.fill();

    // 6) DYNAMIC DISCO DANCE FLOOR SHADOW
    c.filter = "blur(6px)";
    c.fillStyle = "rgba(10, 10, 15, 0.28)";
    const shadowY = FLOOR_Y + 14;
    const shadowX = X_CENTER + swayX * 0.65;
    const shadowW = 95 + dipY * 1.8;
    c.beginPath();
    c.ellipse(shadowX, shadowY, shadowW, 13, 0, 0, Math.PI * 2);
    c.fill();

    c.restore();
    this.texture.needsUpdate = true;
  }

  dispose() {
    if (this.texture) {
      this.texture.dispose();
    }
  }
}
